import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Note, PlusIcon, Select, Spinner, Table, TableBar, Td, Th, formatAgo } from "@app/shared/src/components_v2";
import { Btn } from "@app/shared/src/components_v2";
import { attentionIds } from "../../helpers/deviceAttention";
import { siteLabel } from "../../helpers/deviceLabels";
import { urlEnum, useUrlState } from "../../hooks/useUrlState";
import { AddDeviceWizard } from "../shared/AddDeviceWizard";
import { useFleet } from "../providers/FleetProvider";
import { DeviceStatusTag } from "../shared/DeviceStatusTag";
import { PageTitle } from "../shared/PageTitle";

/** The fleet, one row per unit. Identity is TMA number + device key — these units have no GPS. */

const STALE_MS = 15 * 60 * 1000;
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

type FilterKey = "all" | "online" | "stale" | "attention";

const FILTERS: { value: FilterKey; label: string }[] = [
    { value: "all", label: "All units" },
    { value: "attention", label: "Needs attention" },
    { value: "online", label: "Online now" },
    { value: "stale", label: "Not seen 14 days" },
];

const FILTER_CODEC = urlEnum<FilterKey>(["all", "online", "stale", "attention"]);

export const DevicesPage: React.FC = () => {
    // Trucks and their crew-mates, not every sensor in the org — same scope as Overview counts.
    const { statsDevices: devices, group, isLoading, error } = useFleet();
    const navigate = useNavigate();
    // In the URL so Overview's KPI tiles and its Needs attention card can land here already
    // filtered — and so the filtered list is a place you can come back to.
    const [filter, setFilter] = useUrlState<FilterKey>("filter", "all", FILTER_CODEC);
    const [addOpen, setAddOpen] = useState(false);

    // Search lives in the topbar palette (⌘K), so the table carries filters only.
    const filtered = useMemo(() => {
        // Same helper Overview's card is built from, so "needs attention" cannot mean two things.
        const attention = filter === "attention" ? attentionIds(devices) : undefined;
        return devices.filter((d) => {
            const age = Date.now() - new Date(d.lastHeartbeat ?? 0).getTime();
            if (filter === "online") return age <= STALE_MS;
            if (filter === "stale") return age > TWO_WEEKS_MS;
            if (filter === "attention") return attention!.has(d.id);
            return true;
        });
    }, [devices, filter]);

    if (error) return <Note tone="crit">{error}</Note>;

    return (
        <div className="flex flex-col">
            <TableBar title={<PageTitle>Devices</PageTitle>} count={isLoading ? undefined : `${filtered.length} of ${devices.length}`}>
                <Select value={filter} onChange={(e) => setFilter(e.target.value as FilterKey)}>
                    {FILTERS.map((f) => (
                        <option key={f.value} value={f.value}>
                            {f.label}
                        </option>
                    ))}
                </Select>
                <Btn onClick={() => setAddOpen(true)}>
                    <PlusIcon size={15} />
                    Add device
                </Btn>
            </TableBar>

            <Card>
                {isLoading ? (
                    <div className="grid place-items-center p-10 text-[var(--muted)]">
                        <Spinner />
                    </div>
                ) : filtered.length === 0 ? (
                    <p className="px-5 py-6 text-[13px] text-[var(--muted)]">
                        {filter !== "all"
                            ? "No units match that filter."
                            : group
                              ? `No devices in ${group.name} yet.`
                              : "No devices in this organization yet."}
                    </p>
                ) : (
                    <Table>
                            <thead>
                                <tr>
                                    <Th>TMA</Th>
                                    <Th>Device key</Th>
                                    <Th>Status</Th>
                                    <Th>Firmware</Th>
                                    <Th>Last seen</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((d) => (
                                    <tr
                                        key={d.id}
                                        className="cursor-pointer hover:bg-[var(--surface-2)]"
                                        // Carry the filter through, so backing out of a unit returns
                                        // to the list you were working, not the whole fleet.
                                        onClick={() =>
                                            navigate(`/devices/${d.id}`, {
                                                state: {
                                                    from: filter === "all" ? "/devices" : `/devices?filter=${filter}`,
                                                    fromLabel: FILTERS.find((f) => f.value === filter)?.label,
                                                },
                                            })
                                        }
                                    >
                                        {/* The location names the truck — one site is one TMA is one
                                            unit here — so the old Site column said this twice. */}
                                        <Td label="TMA" className="font-semibold">{siteLabel(d)}</Td>
                                        <Td label="Device key" className="font-mono text-[13px] text-[var(--muted)]">{d.deviceKey}</Td>
                                        <Td label="Status">
                                            <DeviceStatusTag status={d.status} lastHeartbeat={d.lastHeartbeat} />
                                        </Td>
                                        <Td label="Firmware" className="tabular-nums">{d.firmwareVersion ?? "—"}</Td>
                                        <Td label="Last seen" className="tabular-nums text-[var(--muted)]">{formatAgo(d.lastHeartbeat)}</Td>
                                    </tr>
                                ))}
                            </tbody>
                    </Table>
                )}
            </Card>

            <AddDeviceWizard open={addOpen} onClose={() => setAddOpen(false)} />
        </div>
    );
};
